/**
 * O ritual de impressão dos miolos.
 *
 * Papel: espera as fontes, marca o `<body>` com `data-impressao`, espera dois
 * quadros, chama `window.print()` e tira a marca 500 ms depois — a sequência de
 * `exportDashboardPDF` da origem de Manutenções (l. 1163–1180), que o Fechamento
 * de Finanças também segue.
 *
 * Como usar: a tela chama `const imprimir = useImpressao('nome-da-marca')` e
 * `void imprimir()` no clique. O CSS de impressão daquela tela só vale com a
 * marca, pela variante arbitrária do Tailwind
 * `[body[data-impressao=nome-da-marca]_&]:` (por exemplo
 * `print:[body[data-impressao=manutencoes-dashboard]_&]:hidden`). Sem a marca, um
 * Ctrl+P comum imprime a tela como ela é. Dado que precisa entrar no cabeçalho
 * impresso — a data de "Gerado em" — vai para o estado da tela antes de chamar
 * `imprimir`; os dois quadros de espera garantem que o React já o desenhou.
 *
 * As fontes vêm antes de tudo porque, sem elas, o PDF sai com a fonte de
 * reserva no lugar da Outfit.
 */
import { useCallback, useEffect, useRef } from 'react';

/** Quanto tempo a marca fica depois do `print()`, o mesmo da origem. */
const ESPERA_PARA_DESMARCAR_EM_MS = 500;

/**
 * Espera dois quadros de desenho.
 *
 * @returns Promessa cumprida depois do segundo quadro.
 */
function doisQuadros(): Promise<void> {
  return new Promise((cumprir) => {
    requestAnimationFrame(() => requestAnimationFrame(() => cumprir()));
  });
}

/**
 * Devolve a função que imprime a tela com uma marca no `<body>`.
 *
 * @param marca - O nome que o CSS de impressão da tela procura em
 *   `data-impressao`, em kebab-case.
 * @returns `imprimir`, que cumpre depois de chamar `window.print()`.
 */
export function useImpressao(marca: string) {
  const temporizador = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (temporizador.current !== null) window.clearTimeout(temporizador.current);
      // Desmontar dentro dos 500 ms cancela o temporizador que retiraria a marca.
      if (document.body.dataset.impressao === marca) delete document.body.dataset.impressao;
    },
    [marca],
  );

  return useCallback(async () => {
    if ('fonts' in document) await document.fonts.ready;
    // Um pedido novo cancela a retirada pendente do anterior; sem isso, o
    // temporizador do primeiro apagaria a marca no meio do segundo.
    if (temporizador.current !== null) window.clearTimeout(temporizador.current);
    document.body.dataset.impressao = marca;
    await doisQuadros();
    window.print();
    temporizador.current = window.setTimeout(() => {
      if (document.body.dataset.impressao === marca) delete document.body.dataset.impressao;
      temporizador.current = null;
    }, ESPERA_PARA_DESMARCAR_EM_MS);
  }, [marca]);
}
